import { useCallback, useRef, useState } from 'react';

import {
  enhancedApi as providerDirectoryV2GooglePlacesApi,
  type GooglePlacesAutocompletePredictionV2,
} from '@/services/api/generated/providerDirectoryV2GooglePlacesService';
import {
  enhancedApi as providerDirectoryV2IdentityApi,
  type ProviderServiceSearchResultV2,
} from '@/services/api/generated/providerDirectoryV2IdentityService';
import { useLazyGetReviewFoundProvidersQuery } from '@/services/api/treatmentService';
import type { ReviewFoundProviderEntry } from '@/services/types/treatment-types';
import type { ResolveProviderMatch } from '@/utils/providerDraft';

export type { ResolveProviderMatch };

interface UseProviderSearchParams {
  matterId?: string;
  centerLat?: number;
  centerLng?: number;
}

export interface UseProviderSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  // Resolve results (new endpoint)
  resolveMatches: ResolveProviderMatch[];
  // Full resolve results before review-overlap filtering.
  resolveMatchesAll: ResolveProviderMatch[];
  // Whether review results overlap with resolve service IDs.
  hasReviewResolveOverlap: boolean;
  // Review-found providers (matter context only)
  reviewResults: ReviewFoundProviderEntry[];
  // Google Places predictions
  googleResults: GooglePlacesAutocompletePredictionV2[];
  googleWasBroadened: boolean;
  // Session token for Google Details call
  googleSessionToken: string;
  rotateGoogleSessionToken: () => void;
  // State
  isSearching: boolean;
  hasSearched: boolean;
  clear: () => void;
}

const generateSessionToken = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const normalizeId = (id?: string | null) => id?.trim().toLowerCase() ?? null;

export const useProviderResolveSearch = ({
  matterId,
  centerLat,
  centerLng,
}: UseProviderSearchParams = {}): UseProviderSearchReturn => {
  const [query, setQueryInternal] = useState('');
  const [resolveMatches, setResolveMatches] = useState<ResolveProviderMatch[]>([]);
  const [resolveMatchesAll, setResolveMatchesAll] = useState<
    ResolveProviderMatch[]
  >([]);
  const [reviewResults, setReviewResults] = useState<ReviewFoundProviderEntry[]>([]);
  const [googleResults, setGoogleResults] = useState<
    GooglePlacesAutocompletePredictionV2[]
  >([]);
  const [googleWasBroadened, setGoogleWasBroadened] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasReviewResolveOverlap, setHasReviewResolveOverlap] = useState(false);

  const [triggerResolve] =
    providerDirectoryV2IdentityApi.endpoints.providerDirectoryV2Resolve.useLazyQuery();
  const [triggerReview] = useLazyGetReviewFoundProvidersQuery();
  const [triggerGoogleAutocomplete] =
    providerDirectoryV2GooglePlacesApi.endpoints.providerDirectoryV2GooglePlacesAutocomplete.useLazyQuery();

  const timerRef = useRef<number | null>(null);
  const lastQueryRef = useRef('');
  const sessionTokenRef = useRef(generateSessionToken());

  const rotateGoogleSessionToken = useCallback(() => {
    sessionTokenRef.current = generateSessionToken();
  }, []);

  const flattenResolveMatches = useCallback(
    (response: {
      recommended?: ProviderServiceSearchResultV2;
      alternates?: ProviderServiceSearchResultV2[];
    }) => {
      const ordered = [
        response.recommended,
        ...(response.alternates ?? []),
      ].filter(Boolean) as ProviderServiceSearchResultV2[];

      const seen = new Set<string>();
      const deduped = ordered.filter((result) => {
        const id = String(result.provider_service.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      const toAddress = (result: ProviderServiceSearchResultV2) =>
        result.clinical_location?.address ??
        result.records_location?.address ??
        result.billing_location?.address;

      return deduped.map((result) => {
        const address = toAddress(result);
        return {
          provider_service_id: String(result.provider_service.id),
          name: result.provider_service.name,
          formatted_address: address?.formatted_address ?? undefined,
          address_line1: address?.address_line1 ?? undefined,
          address_line2: address?.address_line2 ?? undefined,
          city: address?.city ?? undefined,
          state: address?.state ?? undefined,
          postal_code: address?.postal_code ?? undefined,
          country: undefined,
          phone: result.best_contacts?.phone?.value ?? null,
          subtitle: result.subtitle ?? null,
          verified_contact_count: result.verified_contact_count ?? 0,
          matter_link_count: result.matter_link_count ?? 0,
          has_preferred_records: result.has_preferred_records ?? false,
          has_preferred_bills: result.has_preferred_bills ?? false,
          already_on_matter: result.already_on_matter ?? false,
        };
      });
    },
    [],
  );

  const setQuery = useCallback(
    (q: string) => {
      setQueryInternal(q);

      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const trimmed = q.trim();
      if (trimmed.length < 2) {
        setResolveMatches([]);
        setResolveMatchesAll([]);
        setReviewResults([]);
        setGoogleResults([]);
        setGoogleWasBroadened(false);
        setHasReviewResolveOverlap(false);
        setIsSearching(false);
        lastQueryRef.current = '';
        return;
      }

      setIsSearching(true);

      timerRef.current = window.setTimeout(async () => {
        if (lastQueryRef.current === trimmed) {
          setIsSearching(false);
          return;
        }
        lastQueryRef.current = trimmed;

        try {
          // Fire resolve + review in parallel
          const [resolveResult, reviewResult] = await Promise.all([
            triggerResolve({ q: trimmed, limit: 10, matterId })
              .unwrap()
              .catch(() => ({
                recommended: undefined,
                alternates: [] as ProviderServiceSearchResultV2[],
              })),
            matterId
              ? triggerReview({ matterId, q: trimmed, limit: 10 })
                  .unwrap()
                  .catch(() => [] as ReviewFoundProviderEntry[])
              : Promise.resolve([] as ReviewFoundProviderEntry[]),
          ]);

          const flattenedResolveMatches = flattenResolveMatches(resolveResult);
          const resolveServiceIds = new Set(
            flattenedResolveMatches
              .map((result) => normalizeId(result.provider_service_id))
              .filter(Boolean),
          );
          const reviewRows = reviewResult ?? [];
          const reviewLinkedResolveIds = new Set(
            reviewRows
              .map((result) => normalizeId(result.provider_service_ref_id))
              .filter((id): id is string => Boolean(id && resolveServiceIds.has(id))),
          );
          const filteredResolveMatches = flattenedResolveMatches.filter(
            (result) =>
              !reviewLinkedResolveIds.has(
                normalizeId(result.provider_service_id) ?? '',
              ),
          );

          // If a review-found provider maps to a resolve result, prefer the
          // review entity row so selection uses the existing matter provider.
          setHasReviewResolveOverlap(reviewLinkedResolveIds.size > 0);
          setResolveMatchesAll(flattenedResolveMatches);
          setResolveMatches(filteredResolveMatches);
          setReviewResults(reviewRows);

          // Google Places autocomplete (with broadening fallback)
          let predictions: GooglePlacesAutocompletePredictionV2[] = [];
          let broadened = false;
          try {
            const strictResult = await triggerGoogleAutocomplete({
              q: trimmed,
              sessionToken: sessionTokenRef.current,
              centerLat,
              centerLng,
              radiusM: 50000,
              strictBounds: true,
            }).unwrap();

            predictions = strictResult.predictions ?? [];

            if (
              predictions.length === 0 &&
              centerLat != null &&
              centerLng != null
            ) {
              const broadResult = await triggerGoogleAutocomplete({
                q: trimmed,
                sessionToken: sessionTokenRef.current,
                centerLat,
                centerLng,
                radiusM: 50000,
                strictBounds: false,
              }).unwrap();
              predictions = broadResult.predictions ?? [];
              broadened = true;
            }
          } catch {
            predictions = [];
          }

          setGoogleResults(predictions);
          setGoogleWasBroadened(broadened);
          setHasSearched(true);
        } catch {
          // Keep existing results on error
        } finally {
          setIsSearching(false);
        }
      }, 250);
    },
    [
      triggerResolve,
      triggerReview,
      triggerGoogleAutocomplete,
      matterId,
      centerLat,
      centerLng,
      flattenResolveMatches,
    ],
  );

  const clear = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setQueryInternal('');
    setResolveMatches([]);
    setResolveMatchesAll([]);
    setReviewResults([]);
    setGoogleResults([]);
    setGoogleWasBroadened(false);
    setHasReviewResolveOverlap(false);
    setIsSearching(false);
    setHasSearched(false);
    lastQueryRef.current = '';
    sessionTokenRef.current = generateSessionToken();
  }, []);

  return {
    query,
    setQuery,
    resolveMatches,
    resolveMatchesAll,
    hasReviewResolveOverlap,
    reviewResults,
    googleResults,
    googleWasBroadened,
    googleSessionToken: sessionTokenRef.current,
    rotateGoogleSessionToken,
    isSearching,
    hasSearched,
    clear,
  };
};
